import React, { useState } from 'react';
import {
  Tooltip,
  Box,
  Typography,
  Divider,
  Button,
  Modal,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ITherapy } from 'interfaces/redux/ITherapy';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  tooltipContent: {
    padding: '8px 0',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  therapyItem: {
    'display': 'flex',
    'alignItems': 'flex-start',
    'gap': 0.5,
    'marginBottom': '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  therapyIndex: {
    fontWeight: 700,
    minWidth: '24px',
  },
  therapyName: {
    fontWeight: 700,
  },
  divider: {
    margin: '8px 0',
    backgroundColor: theme.palette.divider,
    opacity: 0.8,
  },
  moreIndicator: {
    marginTop: '8px',
    fontStyle: 'italic',
    fontSize: '12px',
  },
  modalPaper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    padding: '24px',
    overflow: 'auto',
    boxShadow: theme.palette.mode === 'light' ? theme.shadows[5] : theme.shadows[20],
    borderRadius: '8px',
  },
  modalTitle: {
    marginBottom: '16px',
    fontWeight: 700,
    fontSize: '22px',
  },
  therapiesList: {
    maxHeight: '60vh',
    overflow: 'auto',
    marginBottom: '16px',
  },
  therapyItemInModal: {
    'paddingLeft': 0,
    'paddingRight': 0,
    'marginBottom': '12px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  closeButtonStyled: {
    'textTransform': 'uppercase',
    'fontSize': '14px',
    'fontWeight': 500,
    'color': theme.palette.text.primary,
    'padding': '8px 16px',
    'border': '1px solid transparent',
    'backgroundColor': 'transparent',
    'borderRadius': '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

interface TherapiesTooltipProps {
  therapiesList: ITherapy[];
  maxDisplayInTooltip?: number;
  cursor?: 'pointer' | 'default';
  getFillCycleNumber?: (therapyId: number) => number | undefined;
  children: React.ReactNode;
}

const MAX_DISPLAY_DEFAULT = 8;

/**
 * Formats therapy name with strength and FC number
 * Format: Drug Name + Strength (FC# X)
 */
function formatTherapyName(therapy: ITherapy, fcNumber?: number): string {
  const strength = therapy.strength
    ? `${therapy.strength}${therapy.strength_unit_of_measure || ''}`
    : '';
  const fullName = `${therapy.drug_name}${strength ? ` ${strength}` : ''}`;
  const fcSuffix = fcNumber ? ` (FC# ${fcNumber})` : '';
  return `${fullName}${fcSuffix}`;
}

/**
 * TherapiesTooltip component
 *
 * Displays a limited number of therapies (max 8) in a tooltip with names only.
 * Shows (+X) indicator for remaining therapies.
 * When clicked, opens a modal with the complete list.
 */
export const TherapiesTooltip: React.FC<TherapiesTooltipProps> = ({
  therapiesList,
  maxDisplayInTooltip = MAX_DISPLAY_DEFAULT,
  cursor = 'pointer',
  getFillCycleNumber,
  children,
}) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  if (!children) {
    return null;
  }

  if (!therapiesList || therapiesList.length === 0) {
    return <>{children}</>;
  }

  const displayedTherapies = therapiesList.slice(0, maxDisplayInTooltip);
  const hiddenCount = therapiesList.length - maxDisplayInTooltip;
  const hasMoreTherapies = hiddenCount > 0;

  const tooltipContent = (
    <Box className={classes.tooltipContent}>
      {displayedTherapies.map((therapy, idx) => (
        <Box key={`${therapy.id}-${idx}`}>
          <Box className={classes.therapyItem}>
            <Typography className={classes.therapyIndex}>{idx + 1}.</Typography>
            <Typography className={classes.therapyName}>
              {formatTherapyName(therapy, getFillCycleNumber?.(therapy.id))}
            </Typography>
          </Box>
          {idx < displayedTherapies.length - 1 && <Divider className={classes.divider} />}
        </Box>
      ))}
      {hasMoreTherapies && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.moreIndicator}>+{hiddenCount} - Click for more</Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip
        title={tooltipContent}
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
      >
        <Box component="div" onClick={() => setModalOpen(true)} style={{ cursor }}>
          {children}
        </Box>
      </Tooltip>

      <TherapiesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        therapiesList={therapiesList}
        getFillCycleNumber={getFillCycleNumber}
      />
    </>
  );
};

interface TherapiesModalProps {
  open: boolean;
  onClose: () => void;
  therapiesList: ITherapy[];
  getFillCycleNumber?: (therapyId: number) => number | undefined;
}

/**
 * TherapiesModal component
 *
 * Displays the complete list of therapies in a scrollable modal.
 * Shows the same simple format as the tooltip.
 */
const TherapiesModal: React.FC<TherapiesModalProps> = ({
  open,
  onClose,
  therapiesList,
  getFillCycleNumber,
}) => {
  const classes = useStyles();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="therapies-modal-title"
      aria-describedby="therapies-modal-description"
    >
      <Paper className={classes.modalPaper}>
        <Typography id="therapies-modal-title" className={classes.modalTitle}>
          Therapies FC Order ({therapiesList.length})
        </Typography>
        <Divider />

        <Box id="therapies-modal-description" className={classes.therapiesList}>
          <List disablePadding>
            {therapiesList.map((therapy, idx) => (
              <ListItem
                key={`${therapy.id}-${idx}`}
                disableGutters
                className={classes.therapyItemInModal}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                  <Typography sx={{ fontWeight: 700, minWidth: '24px' }}>{idx + 1}.</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatTherapyName(therapy, getFillCycleNumber?.(therapy.id))}
                  </Typography>
                </Box>
                {idx < therapiesList.length - 1 && (
                  <Divider sx={theme => ({ my: 1.5, backgroundColor: theme.palette.divider })} />
                )}
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box className={classes.closeButton}>
          <Button className={classes.closeButtonStyled} onClick={onClose}>
            CLOSE
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TherapiesTooltip;
